import React, { forwardRef, useCallback, useState, type ForwardedRef } from 'react';
import Button from '@atlaskit/button/new';
import UnlinkIcon from '@atlaskit/icon/core/link-broken';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import { OUTWARD_LINK_DIRECTION } from '@atlassian/jira-issue-shared-types/src/common/types/linked-issue-type.tsx';
import type { UIAnalyticsEvent } from '@atlassian/jira-product-analytics-bridge';
import type {
	Dependency,
	DependencyActionParams,
	EventType,
} from '../../../common/types/edit-dependency-modal.tsx';
import messages from './messages.tsx';

const UnlinkDependencyButton = forwardRef(
	(
		{
			dependency,
			onUnlinkDependency,
			focusNextUnlinkButton,
		}: {
			dependency: Dependency;
			onUnlinkDependency: (data: DependencyActionParams) => Promise<void> | void;
			focusNextUnlinkButton: () => void;
		},
		ref: ForwardedRef<HTMLButtonElement>,
	) => {
		const { formatMessage } = useIntl();
		const { issueType, issueKey, issueSummary, link } = dependency;

		const [isLoading, setIsLoading] = useState<boolean>(false);

		const onClick = useCallback(
			async (event: EventType, analyticsEvent: UIAnalyticsEvent) => {
				focusNextUnlinkButton();
				setIsLoading(true);
				await onUnlinkDependency({ dependency, event, analyticsEvent });
				setIsLoading(false);
			},
			[dependency, focusNextUnlinkButton, onUnlinkDependency],
		);

		return (
			<Button
				ref={ref}
				iconBefore={UnlinkIcon}
				isLoading={isLoading}
				appearance="subtle"
				onClick={onClick}
				interactionName="timeline-edit-dependency-unlink-clicked"
				aria-label={formatMessage(
					expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
						? messages.unlinkAriaLabelIssueTermRefresh
						: messages.unlinkAriaLabel,
					{
						issueType,
						issueKey,
						issueSummary,
						linkType:
							link === OUTWARD_LINK_DIRECTION
								? formatMessage(messages.blocks)
								: formatMessage(messages.blockedBy),
					},
				)}
			>
				{formatMessage(messages.unlink)}
			</Button>
		);
	},
);

export default UnlinkDependencyButton;
